// BAFA wizard state — split into the two human-edited form blocks (form1 +
// form2), the derived slugs, and step tracking. Persisted in localStorage so
// reloading mid-wizard keeps the wizard at the same step.

export type BafaReportType = "lead_easy" | "sorhage" | "generic";

export interface Form1State {
  reportType: BafaReportType | "";
  websiteUrl: string;
  mitarbeitende: string;
  kernproblem: string;
  zeitraumStart: string;
  zeitraumEnd: string;
  transkripte: string;
  muendlicheAbsprachen: string;
}

export interface Form2State {
  firmenname: string;
  branche: string;
  inhaber: string;
  standort: string;
}

/**
 * "import" = the wizard is on the new "Daten importieren" step (rendered in
 * front of the existing Angaben/Review/Fragebogen/Bericht steps). The
 * numeric `step` still tracks position inside the original 4-step flow once
 * the user has either submitted an import or skipped past it.
 */
export type BafaPhase = "import" | "wizard";

export interface BafaState {
  phase: BafaPhase;
  step: 1 | 2 | 3 | 4;
  maxReachedStep: 1 | 2 | 3 | 4;
  form1: Form1State;
  form2: Form2State;
  slugFromUrl: string | null;
  currentCompanySlug: string | null;
}

export const EMPTY_FORM1: Form1State = {
  reportType: "",
  websiteUrl: "",
  mitarbeitende: "",
  kernproblem: "",
  zeitraumStart: "",
  zeitraumEnd: "",
  transkripte: "",
  muendlicheAbsprachen: "",
};

export const EMPTY_FORM2: Form2State = {
  firmenname: "",
  branche: "",
  inhaber: "",
  standort: "",
};

export const INITIAL_BAFA_STATE: BafaState = {
  phase: "import",
  step: 1,
  maxReachedStep: 1,
  form1: EMPTY_FORM1,
  form2: EMPTY_FORM2,
  slugFromUrl: null,
  currentCompanySlug: null,
};

export const FIXED_VALUES: Record<BafaReportType, { beratertage: number | null; stunden: number | null }> = {
  lead_easy: { beratertage: 4.5, stunden: 35 },
  sorhage: { beratertage: 5, stunden: 40 },
  generic: { beratertage: null, stunden: null },
};

export const REPORT_TYPE_HINTS: Record<BafaReportType, string> = {
  lead_easy: "LinkedIn/Sales B2B, Vertriebsberatung (Dr. Florian Marthaler).",
  sorhage: "Prozess-/Kommunikationsoptimierung, z. B. Gästekommunikation (Niklas Sorhage).",
  generic:
    "Dynamisch aus Beratervertrag abgeleitete Kapitelstruktur. Kapitel 3 wird individuell per Kapitelkonzeption (Schritt 4b) generiert.",
};

const STORAGE_KEY = "zww-agent.bafa-wizard-state";

export function loadPersistedBafaState(): BafaState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return INITIAL_BAFA_STATE;
    const parsed = JSON.parse(raw) as Partial<BafaState>;
    // Pre-FOR-475 saves had no `phase` field; users mid-wizard had already
    // moved past import implicitly, so default to "wizard".
    const phase: BafaPhase = parsed.phase === "import" ? "import" : "wizard";
    return {
      ...INITIAL_BAFA_STATE,
      ...parsed,
      phase,
      form1: { ...EMPTY_FORM1, ...(parsed.form1 ?? {}) },
      form2: { ...EMPTY_FORM2, ...(parsed.form2 ?? {}) },
    };
  } catch {
    return INITIAL_BAFA_STATE;
  }
}

export function persistBafaState(state: BafaState): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage quota or unavailable; the wizard still works in memory
  }
}

export function clearPersistedBafaState(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
